import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';
import { supabase } from '@/integrations/supabase/client';
import type { TaskAgenda } from '@/types/taskAgenda';
import { runAgendaOrdensServicoSync } from '@/services/taskAgenda/runAgendaOrdensServicoSync';
import { runAgendaChamadosSync } from '@/services/taskAgenda/runAgendaChamadosSync';
import { runAgendaReprocessamentoCustos } from '@/services/taskAgenda/runAgendaReprocessamentoCustos';
import { runAgendaRegrasDesmembramento } from '@/services/taskAgenda/runAgendaRegrasDesmembramento';
import { runAgendaOrcamentosIntranetSync } from '@/services/taskAgenda/runAgendaOrcamentosIntranetSync';
import { shouldRunDailySpSlotNow } from '@/services/taskAgenda/taskAgendaSchedule';
import { markStaleTaskAgendaRuns } from '@/services/taskAgenda/markStaleTaskAgendaRuns';
import { TASK_AGENDA_QUERY_KEY, taskAgendaRunsQueryKey } from '@/hooks/taskAgenda/useTaskAgenda';

/**
 * Dispara rotinas agendadas enquanto o utilizador tem sessão aberta (intervalo ~30s).
 * Horários em America/Sao_Paulo; vários horários por dia em `payload.dailyTimesSp`.
 *
 * Modo duplo:
 *  - reprocessamento_regras_desmembramento → executado pela Edge Function task-agenda-runner
 *    via pg_cron; o browser só executa se shouldRunDailySpSlotNow ainda retornar true
 *    (fallback caso a Edge Function não esteja configurada).
 *  - ordens_servico_sync / chamados_sync / reprocessamento_custos_sync → exigem auth da API
 *    externa; a Edge Function define `pending_auto_run = true` e o browser executa ao
 *    detectar essa flag no próximo tick.
 */
export function TaskAgendaRunner() {
  const { user } = useAuth();
  const { imposto } = useConfig();
  const qc = useQueryClient();
  const runningRef = useRef(false);

  useEffect(() => {
    if (!user || typeof window === 'undefined') return;

    const tick = async () => {
      if (runningRef.current) return;
      const closed = await markStaleTaskAgendaRuns();
      if (closed > 0) {
        qc.invalidateQueries({ queryKey: TASK_AGENDA_QUERY_KEY });
        qc.invalidateQueries({ queryKey: ['task-agenda-runs'] });
      }
      const { data, error } = await supabase.from('task_agenda').select('*').eq('ativo', true);
      if (error) {
        console.warn('[TaskAgendaRunner] Não foi possível ler task_agenda:', error.message);
        return;
      }
      if (!data?.length) return;

      const agendas = data as (TaskAgenda & { pending_auto_run?: boolean })[];
      const due: { agenda: TaskAgenda; slotKey: string }[] = [];

      for (const a of agendas) {
        // Jobs sinalizados pela Edge Function via pg_cron (pending_auto_run)
        if (
          a.pending_auto_run &&
          (a.tipo === 'ordens_servico_sync' ||
            a.tipo === 'chamados_sync' ||
            a.tipo === 'reprocessamento_custos_sync' ||
            a.tipo === 'orcamentos_intranet_sync')
        ) {
          due.push({ agenda: a, slotKey: '__pending__' });
          continue;
        }
        // Fallback: execução browser-based normal (cobre todos os tipos + caso pg_cron indisponível)
        const slot = shouldRunDailySpSlotNow(a);
        if (
          slot.should &&
          (a.tipo === 'ordens_servico_sync' ||
            a.tipo === 'chamados_sync' ||
            a.tipo === 'reprocessamento_custos_sync' ||
            a.tipo === 'reprocessamento_regras_desmembramento' ||
            a.tipo === 'orcamentos_intranet_sync')
        ) {
          due.push({ agenda: a, slotKey: slot.slotKey });
        }
      }
      if (due.length === 0) return;

      runningRef.current = true;
      try {
        for (const { agenda, slotKey } of due) {
          // Limpar flag pending_auto_run antes de executar (idempotência)
          if ((agenda as TaskAgenda & { pending_auto_run?: boolean }).pending_auto_run) {
            await supabase.from('task_agenda').update({ pending_auto_run: false }).eq('id', agenda.id);
          }
          try {
            if (agenda.tipo === 'ordens_servico_sync') {
              await runAgendaOrdensServicoSync(agenda, imposto, {
                automaticSlotKey: slotKey !== '__pending__' ? slotKey : undefined,
              });
              qc.invalidateQueries({ queryKey: ['ordensServico'] });
              qc.invalidateQueries({ queryKey: ['ordens-servico-export'] });
            } else if (agenda.tipo === 'chamados_sync') {
              await runAgendaChamadosSync(agenda, {
                automaticSlotKey: slotKey !== '__pending__' ? slotKey : undefined,
              });
              qc.invalidateQueries({ queryKey: ['chamados'] });
            } else if (agenda.tipo === 'reprocessamento_custos_sync') {
              await runAgendaReprocessamentoCustos(agenda, imposto, {
                automaticSlotKey: slotKey !== '__pending__' ? slotKey : undefined,
              });
            } else if (agenda.tipo === 'reprocessamento_regras_desmembramento') {
              await runAgendaRegrasDesmembramento(agenda, {
                automaticSlotKey: slotKey,
                impostoPercentual: imposto,
              });
              qc.invalidateQueries({ queryKey: ['ordens-servico-table'] });
              qc.invalidateQueries({ queryKey: ['ordensServico'] });
              qc.invalidateQueries({ queryKey: ['ordens-servico-export'] });
            } else if (agenda.tipo === 'orcamentos_intranet_sync') {
              await runAgendaOrcamentosIntranetSync(agenda, {
                automaticSlotKey: slotKey !== '__pending__' ? slotKey : undefined,
              });
              qc.invalidateQueries({ queryKey: ['orcamentos'] });
            }
            qc.invalidateQueries({ queryKey: TASK_AGENDA_QUERY_KEY });
            qc.invalidateQueries({ queryKey: taskAgendaRunsQueryKey(agenda.id) });
          } catch (e) {
            console.error('[TaskAgendaRunner] Rotina falhou:', agenda.tipo, e);
          }
        }
      } finally {
        runningRef.current = false;
      }
    };

    const id = window.setInterval(() => {
      void tick();
    }, 30_000);
    void tick();

    return () => window.clearInterval(id);
  }, [user, imposto, qc]);

  return null;
}
